import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import { FaArrowLeft, FaShieldAlt } from 'react-icons/fa';
import Logo from './Logo';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  usePageTitle('Privacy Policy');
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-4 mb-8">
            <motion.button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
          <div className='flex justify-center items-center gap-4 flex-col'>
            <div className='text-center lg:text-left'>
            <img src="/assets/moon-logo.png" alt="Logo" className="w-32 mx-auto lg:mx-0" />
            </div>
          <div className='text-center lg:text-left'>
            <img src="/assets/png-logo.png" alt="Logo" className="w-40 mx-auto lg:mx-0 pb-2" />
            </div>
            </div>
            {/* <Logo size="large" className="mb-6 justify-center" /> */}
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your privacy is important to us. Please read our privacy policy carefully.
            </p>
          </motion.div>
        </motion.div>

        {/* Content */}
        <motion.div
          className="container mx-auto px-4 pb-20"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="glass rounded-3xl p-8 md:p-12 max-w-4xl mx-auto relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-romantic-purple to-romantic-pink opacity-5" />
            
            <div className="relative z-10 prose prose-invert prose-lg max-w-none">
              <div className="text-gray-300 leading-relaxed space-y-6">
                <p>
                  Datenite Ltd ("Datenite", "we" or "us") are firmly committed to protecting your privacy. You should understand what we do with data relating to you ("personal information") which we collect when you use our mobile application and visit our website, www.datenite.co.uk (the "Platform") and how we use and disclose the information. We are committed to complying with all data protection laws as are applicable to us. The data controller for personal information collected from a visitor to the Platform is Datenite Ltd
                </p>

                <p>
                  By using our Platform or by otherwise giving us your personal information you are accepting the practices described in this Privacy Policy. If you do not agree to this Privacy Policy, please do not use our Platform or give us any of your personal information. We reserve the right to make changes to this Privacy Policy at any time. Any changes will be posted in this Privacy Policy. We encourage you to regularly review this Privacy Policy to make sure you are aware of any changes and how your data may be used.
                </p>

                <p>
                  If you have any questions or comments about the Policy, please contact us on <a href="mailto:help@datenite.co.uk" className="text-romantic-pink hover:text-romantic-blue transition-colors">help@datenite.co.uk</a>. This Policy is incorporated into and is subject to the Terms of use. Your use of Platform and any personal information that you provide on the Platform remains subject to the terms of this Platform Privacy Policy and Datenite Terms of use.
                </p>

                <p>
                  <strong>HOW WE COLLECT YOUR PERSONAL INFORMATION</strong><br />
                  We may collect the following personal information relating to you:
                </p>

                <p>
                  You need to register to use our Platform. When you sign up, you will be required to provide us your identity information and contact details (i.e., your first name, date of birth, phone number and e-mail address) and other personal data that you may wish to share with us.
                </p>

                <p>
                  The Platform automatically collects certain technical information relating to the device you use to visit the Platform. Specifically, we use cookies, web beacons and similar technologies to help us understand your activity on the Platform. A cookie is a small piece of data that a website can send to your browser, which may then be stored on your device. Data that we collect automatically includes your internet protocol ("IP") address, location, your device's identification information, and the actions taken on our Platform (e.g., links that you click) as well as other sites that you may visit. For further information on the cookies and similar technologies we use on our Platform and why we use them, please review our Cookie Policy Notice
                </p>

                <p>
                  We sometimes collect anonymous information from visits to the Platform. Anonymous information (or click stream data) refers to information that does not and will not directly or indirectly reveal your identity, e.g., aggregated data relating to all of our Platform visitors. We may use this data to analyse trends and statistics and to help us provide you with a better experience using our Platform.
                </p>

                <p>
                  If you provide us with personal information of third parties (such as the details of your administrative assistant or your emergency contact), you agree to: (a) notify each individual before sharing their personal information with us, (b) explain that their personal information will be processed in accordance with this Privacy Policy, and (c) obtain their consent, where appropriate.
                </p>

                <p>
                  <strong>WHY WE NEED TO COLLECT YOUR PERSONAL INFORMATION</strong><br />
                  To the extent permitted by applicable law, we use your personal information for various purposes, such as to:
                </p>

                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>provide you with the services and information that you have requested.</li>
                  <li>manage and maintain our relationship with you, including responding to an inquiry, question or comment made by you and providing you with other customer support.</li>
                  <li>communicate important notices to you, such as information about changes to this Platform Privacy Policy and Service updates.</li>
                  <li>carry out analytics and market research and internal reporting to enable us to plan, develop and improve our Platform, our Services and marketing activities. (For example, we may use your IP address to help diagnose problems with our server, to administer the Platform, to refine the Platform based on which features people use most often, to help identify Platform users (such as you) and gather general demographic information about Platform users.)</li>
                  <li>inform you of our new services that we think you will find valuable by sending promotional messages via email, SMS, phone, post and/or other similar communications means. (If you would prefer not to receive these communications, simply follow the unsubscribe instructions on the promotional communications that you receive.)</li>
                  <li>create a profile about you to help us personalize our communications to you, including to ensure that you receive relevant marketing communications based on your actions on this particular Platform or your geographic location based on your IP address.</li>
                  <li>prevent and detect unlawful behaviour, and protect or enforce our legal rights, for example defending us in case of legal disputes.</li>
                </ul>

                <p>
                  <strong>TO WHOM WE GIVE YOUR PERSONAL INFORMATION</strong><br />
                  We will not sell your personal information to anyone outside the Datenite. However, we may need to disclose your personal information to third parties in the following instances:
                </p>

                <p>
                  <strong>Service providers:</strong> We will disclose your personal information to companies that provide services to us, such as companies that assist us in operating our Platform and vendors that assist us with marketing campaigns. The service providers are required to keep your personal information confidential and are not permitted to use your personal information for any other purpose than to carry out the services they are performing for us.
                </p>

                <p>
                  <strong>Third parties to meet applicable legal requirements:</strong> We may need to disclose your personal information to a third party if it is necessary to comply with a legal obligation or the decision of a judicial authority, a public authority or a government body, or if disclosure is necessary for national security, law enforcement or other public interest.
                </p>

                <p>
                  <strong>Business partners:</strong> We will disclose your personal information to our business partners if it is necessary for providing Services to you, such as for certain software and related services.
                </p>

                <p>
                  <strong>Third parties in connection with a business sale:</strong> If we make a sale or transfer of assets, or are otherwise involved in a merger or business/asset transfer, we may transfer your personal information to one or more third parties as part of that transaction.
                </p>

                <p>
                  <strong>Other third parties with your consent:</strong> We may also share your personal information with other third parties when you separately consent to such sharing.
                </p>

                <p>
                  <strong>HOW LONG WE RETAIN YOUR PERSONAL INFORMATION</strong><br />
                  Your personal information will be retained only for so long as reasonably necessary for the purposes set out in this Platform Privacy Policy in accordance with applicable laws, including for the purposes of satisfying any legal, regulatory, accounting or reporting requirements.
                </p>

                <p>
                  <strong>HOW WE PROTECT YOUR PERSONAL INFORMATION</strong><br />
                  Datenite implements appropriate security measures designed to prevent unlawful or unauthorized processing of personal information and accidental loss of or damage to personal information. The data collected via our Platform is stored in a secure server with our ISP who will take periodic backups of such data.
                </p>

                <p>
                  <strong>CHILDREN'S PRIVACY PROTECTION</strong><br />
                  The Platform are not intentionally targeted to, or intended for, children under the age of 18. We do not knowingly collect data relating to children.
                </p>

                <p>
                  <strong>CONTROLLING YOUR PERSONAL INFORMATION</strong><br />
                  If you would like to exercise your right to modify or remove your personal information from our database or request a copy of your personal information (to the extent these rights are provided by law), you can send us an e-mail <a href="mailto:help@datenite.co.uk" className="text-romantic-pink hover:text-romantic-blue transition-colors">help@datenite.co.uk</a> at for your protection, we may need to verify your identity before fulfilling your request. We will respond as soon as reasonably practical and within the time frame specified by applicable law. We reserve the right to deny your request based on applicable law and will inform you if we do so.
                </p>

                <p>
                  <strong>LINKS AND THIRD PARTIES</strong><br />
                  The Platform contains links to other websites, including sites controlled by third parties independent of us. Datenite is not responsible for the privacy practices or the contents of such websites. We encourage you to read the privacy notices of such websites before providing any personal information to them. We do not take responsibility for the opinions of third parties expressed on or through our Website.
                </p>

                <p>
                  <strong>CONTACT US</strong><br />
                  Any questions, concerns or complaints about our processing operations and our commitment to this Platform Privacy Policy should be addressed to Datenites's Data Protection Officer at <a href="mailto:help@datenite.co.uk" className="text-romantic-pink hover:text-romantic-blue transition-colors">help@datenite.co.uk</a> In addition to contacting us, in certain countries you have the right to lodge a complaint with your local data protection authority if you so choose.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
